import { DOMSerializer, Node } from 'prosemirror-model'
import { TextManager } from 'components/VisualStudioPlayground/VisualEditor/TextManager'
import { TextBoxShapeProps } from './Types'
import { TEXT_PROPS } from './constants'
import { getCSSFontVariant } from './helpers'
import { parseRichText } from './richTextDoc'
import { textboxSchema } from './textboxSchema'

// Serialize each block's inline runs to DOM the SAME way RichTextContent renders them, so the box is
// measured against the real per-run markup (bold/italic/weight widths), not the flattened text.
const serializer = DOMSerializer.fromSchema(textboxSchema)

// Indentation per list depth. KEEP IN LOCKSTEP with the editor CSS (`.richText` `--indent-padding:
// depth × 1.3em` in TextBoxShape.module.scss) and monospace's richTextBlockLayout.ts INDENT_FACTOR —
// if these diverge, the box is measured at a different width than the render wraps at.
const INDENT_FACTOR = 1.3

// Bullets cycle per depth. KEEP IN LOCKSTEP with RichTextContent's BULLETS and monospace's
// richTextBlockLayout.ts BULLETS, or the measured marker width won't match the rendered glyph.
const BULLETS = ['•', '◦', '▪']

const listItemDepth = (item: Node): number => {
  const raw = item.attrs?.depth
  const n = typeof raw === 'number' ? raw : Number.parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

export interface RichTextSize {
  width: number
  height: number
}

// Measure a rich-text doc the way the editor lays it out: blocks stack at their line-height with NO
// inter-block margin (`.richText` sets `p, ul, ol { margin: 0 }`), and list items indent by depth. We
// measure each block at its own available width and sum the wrapped heights, counting EVERY visual line
// — including empty interior blocks (an empty paragraph or empty list item between filled ones). The
// plain-text flatten can't do this: ProseMirror's `textBetween` emits its block separator lazily, so an
// empty interior block contributes no newline and the box is measured a line too short (content spills
// out the bottom). Returns null for an unusable doc so the caller falls back to the plain `text` prop.
export const measureRichText = (textManager: TextManager, props: TextBoxShapeProps): RichTextSize | null => {
  const doc = parseRichText(props.richText)
  if (!doc) return null

  const { fontSize: baseFontSize, lineHeight } = props
  const indentStep = baseFontSize * INDENT_FACTOR

  let height = 0
  let width = 0

  // One inline block (paragraph / heading / list-item content) at the given left indent and font size.
  // An empty block still occupies one line — measureText of '' has no line box in the DOM, so size it
  // directly from the line height.
  const measureInline = (node: Node, indent: number, fontSize: number): void => {
    if (node.content.size === 0) {
      height += fontSize * lineHeight
      width = Math.max(width, indent)
      return
    }
    const maxWidth = props.autoSize ? null : Math.max(1, Math.floor(props.w - indent))
    // Measure the real serialized runs (bold/italic/weight) — the base fontWeight/style below is only
    // the block default; each run's own marks override it in the markup, exactly as the canvas draws.
    const box = textManager.measureInlineContent(serializer.serializeFragment(node.content), {
      ...TEXT_PROPS,
      fontFamily: props.fontFamily,
      fontSize,
      fontWeight: `${props.fontWeight}`,
      fontStyle: props.italic ? 'italic' : 'inherit',
      maxWidth,
      lineHeight,
      letterSpacing: props.letterSpacing,
      fontVariant: getCSSFontVariant(props.fontFamily),
      uppercase: props.uppercase,
    })
    height += box.h
    width = Math.max(width, indent + box.w)
  }

  // The marker sits in a gutter to the LEFT of the content: its width is the fixed indentStep unless the
  // marker glyph outgrows it (2+ digit ordered items, wide roman/alpha), in which case the gutter grows to
  // the marker so the content is pushed right instead of the marker overlapping it. Mirrors monospace's
  // markerGutter and the editor's flex gutter. Drawn in the body font (weight, non-italic, base size).
  const markerWidth = (marker: string): number =>
    textManager.measureText(marker, {
      ...TEXT_PROPS,
      fontFamily: props.fontFamily,
      fontSize: baseFontSize,
      fontWeight: `${props.fontWeight}`,
      fontStyle: 'inherit',
      maxWidth: null,
      lineHeight,
      letterSpacing: props.letterSpacing,
      fontVariant: getCSSFontVariant(props.fontFamily),
    }).w

  // The scoped schema has no `heading` node — normalizeForTextboxSchema coerces headings to paragraphs
  // before parse — so there is no heading case here; every non-list block measures at the base size.
  const walk = (node: Node, indent: number): void => {
    switch (node.type.name) {
      case 'unordered_list':
      case 'ordered_list': {
        // Flat depth model: every item carries its own `depth`; content indents by depth × step plus the
        // marker gutter. Ordered counters run per depth and a shallower item restarts the deeper ones —
        // KEEP IN LOCKSTEP with RichTextContent.stashListMarkers and monospace's nextOrderedCount.
        const ordered = node.type.name === 'ordered_list'
        const counters: number[] = []
        node.forEach((item) => {
          const depth = listItemDepth(item)
          let marker: string
          if (ordered) {
            counters[depth] = (counters[depth] ?? 0) + 1
            counters.length = depth + 1
            marker = `${counters[depth]}.`
          } else {
            marker = BULLETS[depth % BULLETS.length]
          }
          const gutter = Math.max(indentStep, markerWidth(marker))
          const itemIndent = depth * indentStep + gutter
          item.forEach((child) => walk(child, itemIndent))
        })
        break
      }
      default:
        if (node.inlineContent) measureInline(node, indent, baseFontSize)
    }
  }

  // The scoped schema forces no trailing paragraph, so every block is real content — measure them all.
  doc.forEach((block) => walk(block, 0))

  return { width, height }
}
